import React, { useCallback } from "react";
import { Form } from "react-final-form";
import { FormattedMessage } from "react-intl";
import { Button } from "reactstrap";
import { SubmissionError } from "redux-form";
import styled from "styled-components";
import { useLocalizedAppContext } from "src/app/hooks/useLocalizedAppContext";
import { ForgetPasswordLink } from "src/authentication/components/ForgetPasswordLink";
import { useAuthenticate } from "src/authentication/hooks/useAuthenticate";
import { useUsernameCookie } from "src/authentication/hooks/useUsernameCookie";
import type {
  AuthenticationOTPContext,
  AuthenticationResultOtp,
} from "src/authentication/objects/Authentication";
import { ReactQueryResult } from "src/shared/components/ReactQueryResult";
import { useFormLoadErrors } from "src/shared/forms/hooks/useFormLoadErrors";
import { EmailInput } from "src/shared/forms/inputs/EmailInputNew";
import TextInputNew from "src/shared/forms/inputs/TextInputNew";
import Messages from "src/shared/messages/components/Messages";
import DetailedMessage, {
  MessageEnum,
} from "src/shared/messages/objects/DetailedMessage";

export const EMAIL = "email";
export const PASSWORD = "password";

export interface IAuthenticationFormData extends FormData {
  [EMAIL]: string;
  [PASSWORD]: string;
}

export interface IAuthenticationFormProps {
  onOtpRequested: (authenticationOtpContext: AuthenticationOTPContext) => void;
}

export const AuthenticationForm = ({
  onOtpRequested,
}: IAuthenticationFormProps) => {
  const { languageCode } = useLocalizedAppContext();
  const authenticationMutation = useAuthenticate();
  const { set: setUsernameCookie } = useUsernameCookie();
  const handleFormSubmit = useCallback(
    async ({
      [EMAIL]: username,
      [PASSWORD]: password,
    }: IAuthenticationFormData) => {
      try {
        const { data: authenticationResult } =
          await authenticationMutation.mutateAsync({
            username,
            password,
            languageCode,
          });
        setUsernameCookie(username);

        if (authenticationResult.isOtp) {
          const { authenticationToken, nonce, mobileNumber } =
            authenticationResult as AuthenticationResultOtp;
          onOtpRequested({
            username,
            authenticationToken,
            nonce,
            mobileNumber,
          });
        }
      } catch (ex) {
        const message = new DetailedMessage(
          <FormattedMessage id="authentication.error" />,
          MessageEnum.ERROR,
        );
        throw new SubmissionError({ _error: message });
      }
    },
    [authenticationMutation, languageCode, onOtpRequested, setUsernameCookie],
  );
  return (
    <Form onSubmit={useFormLoadErrors(handleFormSubmit)}>
      {({ handleSubmit, pristine, submitError }) => (
        <AuthenticationFormStyled onSubmit={handleSubmit}>
          <h2>
            <FormattedMessage id="authentication" />
          </h2>

          <p>
            <FormattedMessage id="authentication.description" />
          </p>

          <Messages messages={submitError} />

          {authenticationMutation.isLoading && (
            <ReactQueryResult {...authenticationMutation} />
          )}

          <EmailInput
            inputID={EMAIL}
            label={<FormattedMessage id="form.email" />}
            validationDisabled={pristine}
            isRequired
          />
          <TextInputNew
            type="password"
            inputID={PASSWORD}
            label={<FormattedMessage id="form.password" />}
            validationDisabled={pristine}
            isRequired
          />

          <p className="authentication-view-forget">
            <ForgetPasswordLink />
          </p>

          <p>
            <Button color="primary">
              <FormattedMessage id="authentication.submit" />
            </Button>
          </p>
        </AuthenticationFormStyled>
      )}
    </Form>
  );
};

const AuthenticationFormStyled = styled.form`
  > p {
    margin-bottom: 50px;
  }
`;
